import React, { useEffect, useState } from 'react';
import { Drawer, Box, IconButton, Typography, Grid } from '@mui/material';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import useWindowsDimension from 'functions/useWindowsDimension';
import { useAtom } from 'jotai';
import accessTokenAtom from 'data/atoms/accessTokenAtom';
import {
  ICompanyViewModel,
  IDepartmentViewModel,
  ThirdPartEnum,
} from 'Interfaces/ICompaniesConfig';

import {
  CloudFactoryEmployeeListViewModel,
  CloudFactoryEmployeeViewModel,
  IImportEmployeeAddViewModel,
} from '../Converts/ICloudFactoryInterfaces';
import { useSnackbar } from 'notistack';
import { CloudFactoryService } from '../APi/CloudFactoryAPI';
import { ImportEmployeesList } from '../ImportEmployeesList';
import userAtom from 'data/atoms/userAtom';

// 🔹 Ny: bruk felles axios-klient mot Bluu-admin API
import { api } from 'http/apiClient';

interface Props {
  open: boolean;
  company: ICompanyViewModel | null;
  onClose: () => void;
}

export const CloudFactoryEmployeeDrawer: React.FC<Props> = ({
  open,
  company,
  onClose,
}) => {
  const { height } = useWindowsDimension();
  const [user] = useAtom(userAtom);
  const [accessToken] = useAtom(accessTokenAtom);
  const [employees, setEmployees] = useState<
    CloudFactoryEmployeeViewModel[] | null
  >(null);
  const [departments] = useState<IDepartmentViewModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const load = async () => {
      setEmployees(null);

      if (!open || !company?.thirdPartyId || !accessToken) return;

      setLoading(true);
      setError(null);

      try {
        // 1) Hent ansatte fra CloudFactory
        const subs = await CloudFactoryService.getEmployees(
          company.thirdPartyId,
          accessToken
        );

        console.log('CloudFactory employees: subs', subs.data);

        enqueueSnackbar(
          `Subscriptions ${company.name}. funnet: ${subs.data.length}`,
          { variant: 'info' }
        );

        // 2) Hent lisenser/produkter per employee og bygg import-liste
        const importList: IImportEmployeeAddViewModel[] = [];

        for (const emp of subs.data) {
          try {
            console.log('CloudFactory employee:', emp);

            const cat = await CloudFactoryService.getEmployeeLicenses(
              company.thirdPartyId,
              emp.id,
              accessToken
            );

            console.log('Employee licenses: cat', cat);

            const newListItem: IImportEmployeeAddViewModel = {
              name: emp.displayName,
              creatorId: user?.id ?? '',
              companyId: company.id,
              phone: emp.phone ?? '+47 000 00 000',
              email: emp.username,
              departmentId: null,
              primaryContact: false,
              legacyId: 0,
              thirdPartyId: emp.id,
              thirdParty: ThirdPartEnum.CloudFactory,
              productIds: [],
              isEmailFoundInBluu: false,
              isNotEmailInCloudFactory: false,
            };

            if (cat.data.length > 0) {
              newListItem.productIds = cat.data.map((p) => p.displayName);
            }

            importList.push(newListItem);

            enqueueSnackbar(
              `Henter produkter for ${emp.displayName}: funnet ${cat.data.length}`,
              { variant: 'info' }
            );
          } catch (inner) {
            console.error('CloudFactory employee catalog error:', inner);
            enqueueSnackbar(`Kunne ikke hente katalog for ${emp.name}`, {
              variant: 'error',
            });
          }
        }

        console.log('Employees import-list:', importList);

        // 3) Sammenlign mot backend (Bluu-admin API)
        //    api-klienten sørger for Authorization + X-Tenant-Id via interceptor
        const response = await api.post<CloudFactoryEmployeeListViewModel>(
          '/Module/CloudFactory/ComparedEmployees',
          {
            numberOfCloudFactoryEmployees: importList.length,
            cloudFactoryEmployees: importList,
          }
        );

        const result = response.data;

        // Filtrer bort ansatte med "EXT" i e-post
        const filtered = result.cloudFactoryEmployees.filter((e) =>
          e.email ? !e.email.toUpperCase().includes('EXT') : true
        );

        setEmployees(filtered);

        enqueueSnackbar(
          `Sjekket ${result.numberOfCloudFactoryEmployees} ansatte mot databasen`,
          { variant: 'success' }
        );
      } catch (err: any) {
        console.error('Feil i CloudFactoryEmployeeDrawer.load:', err);
        setError(err.message || 'Feil under lasting');
        enqueueSnackbar('Feil under lasting av CloudFactory-ansatte', {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, company, accessToken, enqueueSnackbar, user]);

  const handleRemoveEmployees = (
    imported: CloudFactoryEmployeeViewModel[]
  ) => {
    setEmployees((prev) =>
      prev ? prev.filter((p) => !imported.some((i) => i.email === p.email)) : []
    );

    enqueueSnackbar(`Fjernet ${imported.length} employees fra importlisten`, {
      variant: 'info',
    });

    onClose();
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      variant="persistent"
      PaperProps={{ sx: { width: '100%' } }}
      onClose={onClose}
    >
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          sx={{
            flexGrow: 1,
            fontWeight: 'bold',
            fontSize: 20,
            color: 'primary.contrastText',
          }}
        >
          IMPORT EMPLOYEES
        </Typography>
        <IconButton onClick={onClose}>
          <CancelRoundedIcon />
        </IconButton>
      </Box>

      <Grid container sx={{ backgroundColor: 'primary.main' }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'flex-start',
          }}
        >
          <ImportEmployeesList
            expanded
            employees={employees || []}
            departments={departments}
            company={company}
            handleRemoveEmployees={handleRemoveEmployees}
          />
        </Grid>
      </Grid>
    </Drawer>
  );
};
